import React from 'react';
import ItemCard from './ItemCard';
import './ItemsSection.css'

const ItemsSection = ({
  items,
  availableItems,
  showHsnColumn,
  taxType,
  hasdiscount,
  onProductSelect,
  onItemChange,
  onAddItem,
  onRemoveItem,
  getItemTotal,
  calculateItemTax
}) => {
  // Calculate colspan for description row
  let commonTableCols = 4; // Product, Qty, Unit Price, + last column
  if (showHsnColumn) commonTableCols += 1;
  if (taxType !== 'NONE' && hasdiscount) {
    // Tax + Discount: Product, HSN?, Qty, Unit Price, Subtotal
    commonTableCols += 1; // Subtotal
  } else if (taxType !== 'NONE' && !hasdiscount) {
    // Tax + No Discount: Product, HSN?, Qty, Unit Price, Subtotal, Tax, Total
    commonTableCols += 3; // Subtotal, Tax, Total
  } else {
    // No Tax: Product, HSN?, Qty, Unit Price, Total
    commonTableCols += 1; // Total
  }

  return (
    <section className="InvoiceForm__section">
      <h2 className="InvoiceForm__section-title">Items</h2>
      
      {/* Mobile Cards - Always rendered, visibility controlled by CSS */}
      <div className="InvoiceForm__items-mobile-cards">
        {items.map((item, i) => (
          <ItemCard
            key={i}
            item={item}
            index={i}
            availableItems={availableItems}
            showHsnColumn={showHsnColumn}
            taxType={taxType}
            hasdiscount={hasdiscount}
            onProductSelect={onProductSelect}
            onItemChange={onItemChange}
            onRemoveItem={onRemoveItem}
            getItemTotal={getItemTotal}
            calculateItemTax={calculateItemTax}
          />
        ))}
        
        <button 
          onClick={onAddItem} 
          className="InvoiceForm__button InvoiceForm__button--add"
        >
          + Add Item
        </button>
      </div>
      
      {/* Desktop Table - Always rendered, visibility controlled by CSS */}
      <div className="InvoiceForm__items-table-container">
        <table className={`InvoiceForm__items-table ${!showHsnColumn ? 'no-hsn' : ''}`}>
          <thead>
            <tr>
              <th className="InvoiceForm__table-header">Product</th>
              {showHsnColumn && <th className="InvoiceForm__table-header">HSN/SAC</th>}
              <th className="InvoiceForm__table-header">Qty</th>
              <th className="InvoiceForm__table-header">Unit Price</th>
              
              {/* Show Subtotal when there's tax (with or without discount) */}
              {taxType !== 'NONE' && <th className="InvoiceForm__table-header">Subtotal</th>}
              
              {/* Show Tax only when there's tax AND no discount */}
              {taxType !== 'NONE' && !hasdiscount && <th className="InvoiceForm__table-header">Tax</th>}
              
              {/* Show Total when: (no tax) OR (tax AND no discount) */}
              {(taxType === 'NONE' || !hasdiscount) && <th className="InvoiceForm__table-header">Total</th>}
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <React.Fragment key={i}>
                <tr className="InvoiceForm__table-row">
                  <td className="InvoiceForm__table-cell InvoiceForm__table-cell--product">
                    <div className="InvoiceForm__product-with-delete">
                      <ProductInputDropdown
                        item={item}
                        index={i}
                        availableItems={availableItems}
                        onProductSelect={onProductSelect}
                        onItemChange={onItemChange}
                      />
                      <button 
                        className="InvoiceForm__delete-icon" 
                        onClick={() => onRemoveItem(i)}
                        title="Remove item"
                      >
                        <svg 
                          width="14" 
                          height="14" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path 
                            d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M10 11v6M14 11v6" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </td>
                  
                  {showHsnColumn && (
                    <td className="InvoiceForm__table-cell">
                      <input
                        type="text"
                        placeholder="HSN/SAC"
                        className="InvoiceForm__table-input"
                        value={item.hsn_code}
                        maxLength="10"
                        onChange={(e) => onItemChange(i, 'hsn_code', e.target.value)}
                      />
                    </td>
                  )}
                  
                  <td className="InvoiceForm__table-cell">
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="1"
                      className="InvoiceForm__table-input InvoiceForm__table-input--number InvoiceForm__table-input--no-spinner"
                      value={item.quantity || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || (/^\d+$/.test(value) && parseInt(value) >= 1)) {
                          const numValue = value === '' ? '' : Math.max(1, parseInt(value) || 1);
                          onItemChange(i, 'quantity', numValue);
                        }
                      }}
                    />
                  </td>
                  
                  <td className="InvoiceForm__table-cell">
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="0"
                      className="InvoiceForm__table-input InvoiceForm__table-input--number InvoiceForm__table-input--no-spinner"
                      value={item.unit_price || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || (/^\d*\.?\d*$/.test(value) && parseFloat(value) >= 0)) {
                          const numValue = value === '' ? 0 : Math.max(0, parseFloat(value) || 0);
                          onItemChange(i, 'unit_price', numValue);
                        }
                      }}
                    />
                  </td>

                  {/* Show Subtotal when there's tax (with or without discount) */}
                  {taxType !== 'NONE' && (
                    <td className="InvoiceForm__table-cell">
                      <span className="InvoiceForm__table-amount" title={`₹${getItemTotal(item).toFixed(2)}`}>
                        ₹{getItemTotal(item).toFixed(2)}
                      </span>
                    </td>
                  )}
                  
                  {/* Show Tax only when there's tax AND no discount */}
                  {taxType !== 'NONE' && !hasdiscount && (
                    <td className="InvoiceForm__table-cell">
                      <span className="InvoiceForm__table-amount" title={`₹${calculateItemTax(item).toFixed(2)}`}>
                        ₹{calculateItemTax(item).toFixed(2)}
                      </span>
                    </td>
                  )}
                  
                  {/* Show Total when: (no tax) OR (tax AND no discount) */}
                  {(taxType === 'NONE' || !hasdiscount) && (
                    <td className="InvoiceForm__table-cell">
                      <span className="InvoiceForm__table-amount InvoiceForm__table-amount--total" title={`₹${(getItemTotal(item) + calculateItemTax(item)).toFixed(2)}`}>
                        ₹{(getItemTotal(item) + calculateItemTax(item)).toFixed(2)}
                      </span>
                    </td>
                  )}
                </tr>
                
                <tr className="InvoiceForm__table-row InvoiceForm__table-row--description">
                  <td 
                    className="InvoiceForm__table-cell InvoiceForm__table-cell--description" 
                    colSpan={commonTableCols}
                  >
                    <textarea
                      placeholder="Item description (optional)..."
                      className="InvoiceForm__table-textarea InvoiceForm__table-textarea--description"
                      value={item.description}
                      onChange={(e) => onItemChange(i, 'description', e.target.value)}
                    />
                  </td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
        
        <button 
          onClick={onAddItem} 
          className="InvoiceForm__button InvoiceForm__button--add"
        >+ Add Item</button>
      </div>
    </section>
  );
};

// Custom Product Input Dropdown Component
const ProductInputDropdown = ({ item, index, availableItems, onProductSelect, onItemChange }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState(item.product_name || '');
  const dropdownRef = React.useRef(null);

  const filteredItems = availableItems.filter(product =>
    product.product_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    onItemChange(index, 'product_name', value);
    
    if (value === '' || filteredItems.length > 0) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  const handleProductSelect = (product) => {
    setSearchTerm(product.product_name);
    onProductSelect(index, product.product_id);
    setIsOpen(false);
  };

  const handleArrowClick = (e) => {
    e.preventDefault();
    if (!isOpen) {
      setSearchTerm(''); // Clear search to show all products
    }
    setIsOpen(!isOpen);
  };

  const handleInputFocus = () => {
    if (availableItems.length > 0) {
      setSearchTerm(''); // Clear search to show all products
      setIsOpen(true);
    }
  };

  return (
    <div className="InvoiceForm__product-dropdown" ref={dropdownRef}>
      <div className="InvoiceForm__product-input-container">
        <input
          type="text"
          placeholder="Enter product name or select from list"
          className="InvoiceForm__table-input InvoiceForm__table-input--product"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
        />
        <button
          type="button"
          className="InvoiceForm__dropdown-arrow"
          onClick={handleArrowClick}
        >
          <svg
            className={`InvoiceForm__arrow-icon ${isOpen ? 'InvoiceForm__arrow-icon--open' : ''}`}
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 4.5L6 7.5L9 4.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
      
      {isOpen && filteredItems.length > 0 && (
        <div className="InvoiceForm__dropdown-menu">
          {filteredItems.map((product) => (
            <div
              key={product.product_id}
              className="InvoiceForm__dropdown-item"
              onClick={() => handleProductSelect(product)}
            >
              <div className="InvoiceForm__dropdown-item-name">
                {product.product_name}
              </div>
              <div className="InvoiceForm__dropdown-item-price">
                ₹{product.price_selling}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {searchTerm && filteredItems.length === 0 && !isOpen && (
        <div className="InvoiceForm__input-helper">
          <small className="InvoiceForm__helper-text">
            ✓ Custom product name will be used
          </small>
        </div>
      )}
    </div>
  );
};

export default ItemsSection;